"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  clearConversationNoteReaction,
  createConversationNote,
  deleteConversationNote,
  getListConversationNotesQueryKey,
  listConversationNotes,
  setConversationNoteReaction,
  updateConversationNote,
} from "@/lib/api/generated";
import type { ConversationNoteReactionInValue } from "@/lib/api/model";

export function useConversationNotes(id: string, enabled = true) {
  return useQuery({
    queryKey: getListConversationNotesQueryKey(id),
    queryFn: () => listConversationNotes(id),
    enabled,
  });
}

function invalidateNotes(
  qc: ReturnType<typeof useQueryClient>,
  conversationId: string,
) {
  return qc.invalidateQueries({
    queryKey: getListConversationNotesQueryKey(conversationId),
  });
}

export function useCreateConversationNote(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      content: string;
      parent_note_id?: string | null;
    }) =>
      createConversationNote(conversationId, {
        content: vars.content,
        parent_note_id: vars.parent_note_id ?? null,
      }),
    onSuccess: () => invalidateNotes(qc, conversationId),
  });
}

export function useUpdateConversationNote(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { noteId: string; content: string }) =>
      updateConversationNote(vars.noteId, { content: vars.content }),
    onSuccess: () => invalidateNotes(qc, conversationId),
  });
}

export function useDeleteConversationNote(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteConversationNote(noteId),
    onSuccess: () => invalidateNotes(qc, conversationId),
  });
}

export function useSetConversationNoteReaction(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      noteId: string;
      value: ConversationNoteReactionInValue;
    }) => setConversationNoteReaction(vars.noteId, { value: vars.value }),
    onSuccess: () => invalidateNotes(qc, conversationId),
  });
}

export function useDeleteConversationNoteReaction(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => clearConversationNoteReaction(noteId),
    onSuccess: () => invalidateNotes(qc, conversationId),
  });
}
